import React, { useState, useEffect } from "react";
import {
  UXDataTableWithoutBody,
  StyledTableBody,
  StyledTableCell,
  StyledTableCellLink,
  StyledTableRow,
} from "../../components/UXDataTable";
import { applyFiltersToArray, convertDateFormat } from "../../global";
import { featureToggle } from "../../utils/featureToggle";
import { useUser } from "../../context/UserContext";
import { useAuth } from "../../context/AuthContext";
import { Roles } from "../../constants/roles";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreOptions from "@mui/icons-material/MoreVert";
import { Link } from "react-router-dom";
import AssignTechnicianModal from "./AssignTechnicianModal";
import WorkRequest from "../../api/WorkRequest/WorkRequest";
import ModalSimpleButton from "../../components/Modal/ModalSimpleButton";
import Summary from "./PrintableComponents/Summary";
import { pdf } from "@react-pdf/renderer";
import User from "../../api/Admin/User";
import PrintLabel from "../../api/LIMS/PrintLabel";
import PrintInformation from "../../components/PrintInformation";

const MyWorkRequestCols = [
  "ID",
  "Category",
  "Priority",
  "Description",
  "Requester",
  "Assigned To",
  "Requested Date",
  "Requested Completion",
  "Last Acceptable Date",
  "Facility",
  "Business Unit",
  "Status",
  "Parent WR ID",
  "Submitted By",
];

const MySpecialAnalysisCols = [
  "ID",
  "Category",
  "Priority",
  "Description",
  "Requester",
  "Assigned To",
  "Requested Date",
  "Requested Completion",
  "Last Acceptable Date",
  "Business Unit",
  "Status",
  "Parent WR ID",
  "Submitted By",
];

const TableMyOpenRequests = ({
  filters,
  myRequests,
  reloadWorRequest,
  isDataLoading,
  userEmail,
  isProcessing,
  billingList,
  copyWorkRequest,
  isSpecialAnalysis,
}) => {
  const [assignTechnicianModal, setAssignTechnicianModal] = useState(false);

  const [printLabelInfo, setPrintLabelInfo] = React.useState(null);
  const [printInfoOpen, setPrintInfoOpen] = React.useState(false);
  const [clickedMenuId, setClickedMenuId] = useState(null);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [moreOptionsSelected, setMoreOptionsSelected] = useState(null);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const [modalMessagesOpen, setModalMessagesOpen] = useState(false);
  const [modalMessagesText, setModalMessagesText] = useState("");
  const [modalMessagesTitle, setModalMessagesTitle] = useState("");
  const [modalMessagesButtonText, setModalMessagesButtonText] = useState("Ok");

  const { username } = useUser();
  const { hasAnyRole } = useAuth();
  const canAssignWorkRequest = hasAnyRole([Roles.WorkRequestWorker, Roles.Developer]);

  const noDataMessage = `No open work requests found for ${userEmail}`;

  useEffect(() => {
    var filteredArray = applyFiltersToArray(filters, myRequests);
    setFilteredRequests(filteredArray);
  }, [filters, myRequests]);

  function handleOpenMoreOptions(e, workRequest) {
    setMoreOptionsSelected(workRequest);

    setAnchorEl(e.currentTarget);
  }

  function handleCloseMoreOptions() {
    setMoreOptionsSelected(null);

    setAnchorEl(null);
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const determineStartArray = () => {
    return filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  function closeModalMessages() {
    setModalMessagesOpen(false);
    setModalMessagesText("");
    setModalMessagesButtonText("");
    setModalMessagesTitle("");
  }
  const openModalMessage = (title, message, btnText) => {
    setModalMessagesOpen(true);
    setModalMessagesText(message);
    setModalMessagesButtonText(btnText);
    setModalMessagesTitle(title);
  };
  function closePrintInformation() {
    setPrintInfoOpen(false);
  }

  const assignTechnician = (assignTechnicianEmail) => {
    WorkRequest.updateAssignTo(clickedMenuId, assignTechnicianEmail)
      .then((res) => {
        if (res?.message !== "Success") {
          openModalMessage(
            "Fail",
            `There was an error updating technician. ${res?.message ? res?.message : ""}`,
            "Ok",
          );
        } else {
          reloadWorRequest();
          openModalMessage("Success", "Technician successfully updated", "Close");
        }
      })
      .catch((err) => {
        openModalMessage("Error", "There was an error updating technician", "Close");
      });
  };

  const deleteWorkRequest = () => {
    if (!clickedMenuId) return;

    WorkRequest.newDeleteWorkRequest(clickedMenuId)
      .then((res) => {
        if (res?.message !== "Success") {
          openModalMessage(
            "Fail",
            `There was an error deleting work request. ${res?.message ? res?.message : ""}`,
            "Ok",
          );
        } else {
          reloadWorRequest();
          openModalMessage("Success", "Work request successfully deleted", "Close");
        }
      })
      .catch((err) => {
        openModalMessage("Error", "There was an error deleting work request", "Close");
      });
  };

  const currentRequest = myRequests?.find((x) => x.id === clickedMenuId) ?? null;

  async function getSummary() {
    if (moreOptionsSelected && moreOptionsSelected.id) {
      let businessPhones = "";

      const userPreferences = await User.searchByEmailAddress(username);

      if (
        userPreferences &&
        userPreferences.businessPhones &&
        userPreferences.businessPhones.length > 0
      ) {
        businessPhones = userPreferences.businessPhones.join(", ");
      }

      WorkRequest.getById(moreOptionsSelected.id).then((response) => {
        if (response) {
          const asPdf = pdf([]);
          asPdf.updateContainer(
            <Summary
              workRequest={response}
              businessPhones={businessPhones}
              billingList={billingList}></Summary>,
          );

          asPdf.toBlob().then((blob) => {
            var url = window.URL.createObjectURL(blob, { oneTimeOnly: true });
            const link = document.createElement("a");
            link.href = url;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          });
        }
      });
    }
  }

  function openPrintInfo(sample, message = "") {
    let arrayPrintInfo = [];
    sample.forEach((sample) => {
      let newPrintInfo = new PrintLabel({
        sampleName: sample.sampleName,
        containerNumbers: [sample.existingContainer.containerNumber],
        includeAdditionalSampleInformation: false,
        chemIDOnly: false,
        printLabelSizeType: null,
        isBlindCoded: false,
        blindDescription: null,
        blindSampleName: null,
        isShelfLabel: false,
        shelfLabelText: null,
        includeShelfBarcode: false,
      });

      arrayPrintInfo.push(newPrintInfo);
    });

    setPrintLabelInfo(arrayPrintInfo);
    setPrintInfoOpen(true);
  }

  const reprintLabel = (id) => {
    WorkRequest.getById(id).then((result) => {
      if (!result.workRequestSamples.length) {
        openModalMessage(
          "No samples to print",
          "The selected request has no samples to be printed",
          "Close",
        );
        return;
      }
      openPrintInfo(result.workRequestSamples);
    });
  };

  return (
    <div>
      <UXDataTableWithoutBody
        tableWidth="100%"
        cols={
          isSpecialAnalysis
            ? MySpecialAnalysisCols.filter(
                (f) => (!isProcessing && f !== "Business Unit") || isProcessing,
              )
            : MyWorkRequestCols.filter(
                (f) => (!isProcessing && f !== "Business Unit") || isProcessing,
              )
        }
        blankFirstHeader={true}
        tableId={`allStudies`}
        rowLength={filteredRequests.length}
        enablePaging={true}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        noDataFoundMessage={noDataMessage}
        isDataLoading={isDataLoading}>
        <StyledTableBody key={"allRequestTablebody"}>
          {determineStartArray()
            .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
            .map((workRequest, index) => {
              return (
                <StyledTableRow key={`table-row-${index}`}>
                  <StyledTableCell style={{ width: "10px" }}>
                    <MoreOptions
                      onClick={(e) => {
                        setClickedMenuId(workRequest.id);

                        handleOpenMoreOptions(e, workRequest);
                      }}
                      color="black"
                    />
                  </StyledTableCell>
                  <StyledTableCellLink
                    scope="row"
                    component={Link}
                    to={{
                      pathname: isSpecialAnalysis
                        ? `/viewSpecialAnalysis/${workRequest.id}`
                        : `${"/newWorkRequestRedesign/"}${workRequest.id}`,
                      state: { tab: isProcessing ? "process" : "submit" },
                    }}>
                    {workRequest.id}
                  </StyledTableCellLink>
                  <StyledTableCell align="right">{workRequest.typeName}</StyledTableCell>
                  <StyledTableCell align="right">{workRequest.priorityName}</StyledTableCell>
                  <StyledTableCell align="right">{workRequest.description}</StyledTableCell>
                  <StyledTableCell align="right">{workRequest.requestedByEmail}</StyledTableCell>
                  <StyledTableCell align="right">{workRequest.assignedToEmail}</StyledTableCell>
                  <StyledTableCell align="right">
                    {convertDateFormat(workRequest.createdDate, false)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {convertDateFormat(workRequest.requestedCompletionDate, false)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {convertDateFormat(workRequest.lastAcceptableCompletionDate, false)}
                  </StyledTableCell>
                  {!isSpecialAnalysis && (
                    <StyledTableCell align="right">{workRequest.facilityName}</StyledTableCell>
                  )}
                  {isProcessing && (
                    <StyledTableCell align="right">{workRequest.businessUnitName}</StyledTableCell>
                  )}
                  <StyledTableCell align="right">{workRequest.statusName}</StyledTableCell>
                  <StyledTableCellLink
                    scope="row"
                    component={Link}
                    to={{
                      pathname: isSpecialAnalysis
                        ? `/viewSpecialAnalysis/${workRequest.parentWorkRequestID}`
                        : `${"/newWorkRequestRedesign/"}${workRequest.parentWorkRequestID}`,
                      state: { tab: isProcessing ? "process" : "submit" },
                    }}>
                    {workRequest.parentWorkRequestID}
                  </StyledTableCellLink>
                  <StyledTableCell align="right">{workRequest.createdByEmail}</StyledTableCell>
                </StyledTableRow>
              );
            })}
        </StyledTableBody>
      </UXDataTableWithoutBody>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={anchorEl && moreOptionsSelected ? true : false}
        onClose={() => handleCloseMoreOptions(null)}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}>
        {isProcessing && (
          <>
            <MenuItem
              onClick={() => {
                getSummary();
                handleCloseMoreOptions();
              }}>
              Print
            </MenuItem>
            <MenuItem
              onClick={() => {
                reprintLabel(currentRequest.id);
                handleCloseMoreOptions();
              }}
              disabled={false}>
              Reprint Work Request Label
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAssignTechnicianModal(true);
                handleCloseMoreOptions();
              }}
              disabled={
                (!(
                  currentRequest?.statusName === "Submitted" &&
                  currentRequest?.createdByEmail === userEmail
                ) &&
                  !(currentRequest?.assignedToEmail === userEmail)) ||
                !canAssignWorkRequest
              }>
              Assign
            </MenuItem>
          </>
        )}
        {(isProcessing ? featureToggle.CopyWorkRequestProcess : featureToggle.CopyWorkRequest) && (
          <MenuItem
            onClick={() => {
              copyWorkRequest(currentRequest.id);
              handleCloseMoreOptions();
            }}>
            Copy Work Request
          </MenuItem>
        )}
        {currentRequest?.statusName === "Draft" && (
          <MenuItem
            onClick={() => {
              deleteWorkRequest();
              handleCloseMoreOptions();
            }}>
            Delete Draft Work Request
          </MenuItem>
        )}
      </Menu>
      {clickedMenuId > 0 && (
        <AssignTechnicianModal
          workRequestId={clickedMenuId}
          assignTechnician={assignTechnician}
          assignTechnicianModal={assignTechnicianModal}
          setAssignTechnicianModal={setAssignTechnicianModal}
        />
      )}
      <ModalSimpleButton
        title={modalMessagesTitle}
        buttonText={modalMessagesButtonText}
        buttonAction={closeModalMessages}
        open={modalMessagesOpen}
        setOpen={setModalMessagesOpen}>
        <label>{modalMessagesText}</label>
      </ModalSimpleButton>
      <PrintInformation
        open={printInfoOpen}
        setOpen={setPrintInfoOpen}
        button2Action={closePrintInformation}
        button2Text={"Close"}
        printLabelInfo={printLabelInfo}
        isShelfLabel={false}></PrintInformation>
    </div>
  );
};

export default TableMyOpenRequests;
